import { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import RequireAuth from './components/auth/RequireAuth';
import LoginCallbackHandler from './components/auth/LoginCallbackHandler';
import AdminRoute from './components/AdminRoute';
import Dashboard from './pages/Dashboard';
import { useAuth } from './hooks/useAuth';
import { getOktaAuth, isOktaConfigured } from './config/auth';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import { lazyPage } from './utils/lazyPage';

// Pages are code-split: each loads on first visit instead of all of them
// shipping in one bundle up front. Dashboard (the landing page) stays eager.
const Admin = lazyPage(() => import('./pages/Admin'));
const Endpoints = lazyPage(() => import('./pages/Endpoints'));
const CreateEndpoint = lazyPage(() => import('./pages/CreateEndpoint'));
const Requests = lazyPage(() => import('./pages/Requests'));
const CreateRequest = lazyPage(() => import('./pages/CreateRequest'));
const RequestDetails = lazyPage(() => import('./pages/RequestDetails'));
const Collections = lazyPage(() => import('./pages/Collections'));
const CreateCollection = lazyPage(() => import('./pages/CreateCollection'));
const CollectionDetails = lazyPage(() => import('./pages/CollectionDetails'));
const Settings = lazyPage(() => import('./pages/Settings'));
const DataVerification = lazyPage(() => import('./pages/DataVerification'));
const DataVerificationSettingsPage = lazyPage(() => import('./pages/DataVerificationSettingsPage'));
const SchemaVerificationSettingsPage = lazyPage(() => import('./pages/SchemaVerificationSettingsPage'));
const TablesList = lazyPage(() => import('./pages/TablesList'));
const SchemaValidationCreate = lazyPage(() => import('./pages/SchemaValidationCreate'));
const DataQualityCreate = lazyPage(() => import('./pages/DataQualityCreate'));
const RowCountCreate = lazyPage(() => import('./pages/RowCountCreate'));
const CreateTests = lazyPage(() => import('./pages/CreateTests'));
const TestHistory = lazyPage(() => import('./pages/TestHistory'));
const TestRunDetails = lazyPage(() => import('./pages/TestRunDetails'));
const TableTestResults = lazyPage(() => import('./pages/TableTestResults'));
const Issues = lazyPage(() => import('./pages/Issues'));
const Schedules = lazyPage(() => import('./pages/Schedules'));
const DataLakeTesting = lazyPage(() => import('./pages/DataLakeTesting'));
const Profile = lazyPage(() => import('./pages/Profile'));

// Shown in the content area while a page's code is downloading.
const PageLoading = () => (
  <div className="loading-state">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

// Placeholder pages for new routes
const Projects = () => <div className="page-content"><h2>Projects</h2><p>Coming soon...</p></div>;
const Metrics = () => <div className="page-content"><h2>Metrics</h2><p>Coming soon...</p></div>;
const Help = () => <div className="page-content"><h2>Help</h2><p>Coming soon...</p></div>;

/**
 * Friendly "not configured" screen so a misconfigured deploy renders an
 * instructive page instead of crashing at `new OktaAuth(...)`.
 */
const AuthNotConfigured = () => (
  <div className="container main-content" style={{ padding: '4rem 1rem' }}>
    <div className="alert alert-warning" role="alert" style={{ maxWidth: 640 }}>
      <h4 className="alert-heading">Authentication not configured</h4>
      <p>
        Okta environment variables are missing. Set the following in your{' '}
        <code>.env</code> and restart the dev server:
      </p>
      <ul>
        <li><code>VITE_OKTA_ISSUER</code></li>
        <li><code>VITE_OKTA_CLIENT_ID</code></li>
        <li><code>VITE_OKTA_REDIRECT_URI</code></li>
      </ul>
      <hr />
      <p className="mb-0">See <code>.env.example</code> for details.</p>
    </div>
  </div>
);

function AppShell() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'API Testing Dashboard';
    if (path === '/data-lake') return 'Data Lake Testing';
    if (path === '/endpoints') return 'Endpoints';
    if (path === '/endpoints/new') return 'Create Endpoint';
    if (path.startsWith('/endpoints/') && path !== '/endpoints/new') return 'Edit Endpoint';
    if (path === '/projects') return 'Projects';
    if (path === '/collections') return 'Collections';
    if (path === '/collections/new') return 'Create Collection';
    if (path.match(/^\/collections\/\d+\/edit$/)) return 'Edit Collection';
    if (path.match(/^\/collections\/\d+$/)) return 'Collection Details';
    if (path === '/metrics') return 'Metrics';
    if (path === '/requests') return 'Requests';
    if (path === '/requests/new') return 'Create Request';
    if (path.match(/^\/requests\/\d+\/details$/)) return 'Request Details';
    if (path.startsWith('/requests/') && path !== '/requests/new' && !path.includes('/details')) return 'Edit Request';
    if (path === '/data-verification') return 'Data Verification';
    if (path === '/data-verification/schema-validation/new') return 'Create Schema Validation Test';
    if (path === '/data-verification/data-quality/new') return 'Create Data Quality Test';
    if (path === '/data-verification/row-count/new') return 'Create Row Count Test';
    if (path === '/data-verification/create') return 'Create Tests';
    if (path === '/tables') return 'Tables';
    if (path.match(/^\/tables\/\d+\/test-results$/)) return 'Test Results';
    if (path.match(/^\/tables\/\d+\/test-history$/)) return 'Test History';
    if (path.match(/^\/tests\/runs\/\d+$/)) return 'Test Details';
    if (path === '/issues') return 'Issues';
    if (path === '/schedules') return 'Schedules';
    if (path === '/admin') return 'Admin';
    if (path === '/settings/data-verification') return 'Data Verification';
    if (path === '/settings/schema-verification') return 'Schema Verification';
    if (path === '/settings') return 'Settings';
    if (path === '/help') return 'Help';
    if (path === '/profile') return 'Profile';
    return 'Dashboard';
  };

  const isCallback = location.pathname === '/login/callback';

  return (
    <EnvironmentProvider>
      <div className="app-container">
        {isAuthenticated && !isCallback && <Sidebar />}

        <div className={`app-main ${isAuthenticated && !isCallback ? 'with-sidebar' : ''}`}>
          {isAuthenticated && !isCallback && <Header title={getPageTitle()} />}

          <div className="app-content">
            <Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/login/callback" element={<LoginCallbackHandler />} />

              <Route element={<RequireAuth />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/data-lake" element={<DataLakeTesting />} />
                <Route path="/endpoints" element={<Endpoints />} />
                <Route path="/endpoints/new" element={<CreateEndpoint />} />
                <Route path="/endpoints/:id" element={<CreateEndpoint />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/collections/new" element={<CreateCollection />} />
                <Route path="/collections/:id/edit" element={<CreateCollection />} />
                <Route path="/collections/:id" element={<CollectionDetails />} />
                <Route path="/metrics" element={<Metrics />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/requests/new" element={<CreateRequest />} />
                <Route path="/requests/:id/details" element={<RequestDetails />} />
                <Route path="/requests/:id" element={<CreateRequest />} />
                <Route path="/data-verification" element={<DataVerification />} />
                <Route path="/data-verification/schema-validation/new" element={<SchemaValidationCreate />} />
                <Route path="/data-verification/data-quality/new" element={<DataQualityCreate />} />
                <Route path="/data-verification/row-count/new" element={<RowCountCreate />} />
                <Route path="/data-verification/create" element={<CreateTests />} />
                <Route path="/tables" element={<TablesList />} />
                <Route path="/tables/:tableId/test-results" element={<TableTestResults />} />
                <Route path="/tables/:tableId/test-history" element={<TestHistory />} />
                <Route path="/tests/runs/:runId" element={<TestRunDetails />} />
                <Route path="/issues" element={<Issues />} />
                <Route path="/schedules" element={<Schedules />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/data-verification" element={<DataVerificationSettingsPage />} />
                <Route path="/settings/schema-verification" element={<SchemaVerificationSettingsPage />} />
                <Route path="/help" element={<Help />} />
                <Route path="/profile" element={<Profile />} />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Admin />
                    </AdminRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
            </Suspense>
          </div>
        </div>
      </div>
    </EnvironmentProvider>
  );
}

function App() {
  const navigate = useNavigate();

  if (!isOktaConfigured()) {
    return <AuthNotConfigured />;
  }

  const oktaAuth = getOktaAuth();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
      replace: true,
    });
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <AppShell />
    </Security>
  );
}

export default App;
